import type { HTMLAttributes, ReactNode } from 'react';
import styles from './card.module.css';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Flat surface — no shadow. For cards inside cards. */
  flush?: boolean;
  /** Recessed surface, for secondary panels. */
  inset?: boolean;
  children: ReactNode;
}

export function Card({ flush, inset, className, children, ...rest }: CardProps) {
  const classes = [styles.card, flush ? styles.flush : null, inset ? styles.inset : null, className]
    .filter(Boolean)
    .join(' ');
  return (
    <div {...rest} className={classes}>
      {children}
    </div>
  );
}

export interface CardHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  /** Renders the title inside the given heading level for document outline. */
  as?: 'h2' | 'h3' | 'h4';
}

export function CardHeader({ title, subtitle, actions, as: Tag = 'h3' }: CardHeaderProps) {
  return (
    <div className={styles.header}>
      <div className={styles.headerText}>
        <Tag className={styles.title}>{title}</Tag>
        {subtitle ? <span className={styles.subtitle}>{subtitle}</span> : null}
      </div>
      {actions ? <div className={styles.actions}>{actions}</div> : null}
    </div>
  );
}

export function CardBody({
  children,
  tight = false,
  className,
}: {
  children: ReactNode;
  tight?: boolean;
  className?: string;
}) {
  return (
    <div
      className={[styles.body, tight ? styles.bodyTight : null, className]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </div>
  );
}

export function CardFooter({ children }: { children: ReactNode }) {
  return <div className={styles.footer}>{children}</div>;
}
